import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';
import { type TrendDirection, TrendIcon } from '@shared/ui/trend-icon';

import {
  LAYOUT_CLASS,
  STAT_CARD_ICON_CIRCLE_CLASS,
  type StatCardLayout,
  type StatCardSurface,
  type StatCardTone,
  type StatCardValueSize,
  SURFACE_CLASS,
  VALUE_SIZE_CLASS,
  VALUE_TONE_CLASS,
} from './stat-card.constant';

export interface StatCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  /** Figma `Title` — 작은 제목 */
  title: ReactNode;
  /** Figma `Value` — 큰 값(단위까지 포함한 글자). 한 줄로 그린다(줄바꿈 없음) */
  value: ReactNode;
  /** Figma `value`(md 18 · lg 20). 기본 md */
  valueSize?: StatCardValueSize;
  /** Figma `tone` — 값 글자 색(default 검정 · primary). 기본 default */
  tone?: StatCardTone;
  /** Figma `surface`. 기본 white */
  surface?: StatCardSurface;
  /** Figma `layout`. 기본 vertical */
  layout?: StatCardLayout;
  /** Figma `Show help` — 제목 옆 도움말 아이콘(12). 기본 켬 */
  showHelp?: boolean;
  /** 도움말 자리에 끼울 것(툴팁을 단 아이콘 등). 없으면 help-circle 아이콘 */
  help?: ReactNode;
  /** Figma `Show trend` — 값 옆 증감 표시 */
  trend?: TrendDirection;
  /**
   * 증감 표시(TrendIcon)를 보조기기가 읽는 이름. 기본은 방향 이름(상승 · 하락 · 변동 없음).
   * 무엇이 오르고 내렸는지 알려 줄 때(`"전 분기보다 상승"`) 준다. 빈 글자면 읽지 않는다(옆 글자가 이미 말할 때)
   */
  trendLabel?: string;
  /** Figma `Show footer` + `Footer` — 값 아래 보조 글자(좁으면 줄바꿈) */
  footer?: ReactNode;
  /** Figma `Show icon` — 오른쪽 원형 아이콘 자리 안에 넣을 아이콘(`<Icon size="lg" name="…" />`) */
  icon?: ReactNode;
  className?: string;
}

/**
 * 지표 카드의 공통 바탕. 제목 + 도움말 → 값(+ 증감) → 보조 글자, 오른쪽 원형 아이콘.
 * 너비는 부모를 채운다(Figma 예시 280).
 */
export function StatCard({
  title,
  value,
  valueSize = 'md',
  tone = 'default',
  surface = 'white',
  layout = 'vertical',
  showHelp = true,
  help,
  trend,
  trendLabel,
  footer,
  icon,
  className,
  ...rest
}: StatCardProps) {
  return (
    <div
      data-surface={surface}
      data-layout={layout}
      data-value-size={valueSize}
      data-tone={tone}
      {...rest}
      className={cn(
        'flex w-full items-center justify-between gap-4 px-5 py-4',
        SURFACE_CLASS[surface],
        className,
      )}
    >
      <div className={cn('flex min-w-0 flex-1', LAYOUT_CLASS[layout])}>
        <div className="flex items-center gap-1">
          <span className="typo-caption text-fg-muted">{title}</span>
          {showHelp
            ? (help ?? <Icon name="help-circle" size="xs" className="text-fg-muted" />)
            : null}
        </div>
        <div className="flex items-center gap-1">
          {/* 값은 줄을 바꾸지 않는다(Figma 값 글자 = 너비 HUG, DD-19). 글꼴이 Pretendard 라 Inter 보다 조금 넓다 */}
          <span
            className={cn('whitespace-nowrap', VALUE_TONE_CLASS[tone], VALUE_SIZE_CLASS[valueSize])}
          >
            {value}
          </span>
          {trend ? (
            <TrendIcon
              direction={trend}
              {...(trendLabel === ''
                ? { 'aria-hidden': true }
                : trendLabel !== undefined
                  ? { 'aria-label': trendLabel }
                  : {})}
            />
          ) : null}
        </div>
        {footer ? <p className="typo-caption break-words text-fg-muted">{footer}</p> : null}
      </div>
      {icon ? (
        <span data-slot="icon" className={STAT_CARD_ICON_CIRCLE_CLASS}>
          {icon}
        </span>
      ) : null}
    </div>
  );
}
